<template>
  <div class="QuotaDisplay">
    <div class="QuotaDisplay-header">
      <span class="QuotaDisplay-title">{{ t('ui.subscription.details.quota.title') }}:</span>

      <Tag
        size="sm"
        :leftIcon="quota.unlimited ? 'infinity' : ''"
      >
        {{ tagText }}
      </Tag>
    </div>

    <div class="QuotaDisplay-card">
      <div class="QuotaDisplay-content">
        <div class="QuotaDisplay-quotaRow">
          <div class="QuotaDisplay-usedSection">
            <span class="QuotaDisplay-usedLabel">{{ t('ui.subscription.details.quota.usedLabel') }}</span>

            <div class="QuotaDisplay-quotaValue">
              <span class="QuotaDisplay-quotaNumber">{{ quota.used }}/</span>
              <Icon
                v-if="quota.unlimited"
                icon="infinity"
                :size="20"
                class="QuotaDisplay-quotaInfinity"
              />
              <span v-else class="QuotaDisplay-quotaNumber">{{ quota.limit }}</span>
            </div>
          </div>

          <div class="QuotaDisplay-period">
            <span class="QuotaDisplay-periodText">
              {{ t('ui.subscription.details.quota.period', { start: periodStartFormatted, end: periodEndFormatted }) }}
            </span>
          </div>
        </div>

        <div class="QuotaDisplay-footer">
          <span class="QuotaDisplay-tripType">{{ t(`ui.subscription.details.quota.types.${tripType}`) }}</span>

          <div class="QuotaDisplay-resetInfo">
            <Icon icon="cached" :size="16" class="QuotaDisplay-resetIcon" />
            <span class="QuotaDisplay-resetText">{{ t('ui.subscription.details.quota.resetsOn' , { date: resetDateFormatted }) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { parseISO, addSeconds } from 'date-fns';
import { formatDate } from '@cvo/domain';
import Icon from '../../atoms/Icon/Icon.vue';
import Tag from '../../atoms/Tag/Tag.vue';
import type { QuotaDisplayProps } from './QuotaDisplay.model';

const props = defineProps<QuotaDisplayProps>();

const { t, locale } = useI18n();

const tagText = computed(() => {
  if (props.quota.unlimited) return t('ui.subscription.details.quota.unlimited');
  return t('ui.subscription.details.quota.limited');
});
const periodStartFormatted = computed(() => {
  if(!props.quota.periodStart) return '';
  return formatDate(parseISO(props.quota.periodStart), locale.value, { dateStyle: 'long' });
});
const periodEndFormatted = computed(() => {
  return formatDate(parseISO(props.quota.periodEnd), locale.value, { dateStyle: 'long' });
});
const resetDateFormatted = computed(() => {
  const date = addSeconds(parseISO(props.quota.periodEnd), 1);
  return formatDate(date, locale.value, { dateStyle: 'long' });
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .QuotaDisplay {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;

    &-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: vars.$dimensions-d-2;
    }

    &-title {
      @include text.text('md', vars.$typography-weight-strong);
      color: vars.$organisms-card-contentcard-details-label-color;
    }

    .Tag {
      --tag-bg-color: #{vars.$color-neutral-gray-50};
      --tag-border-color: #{vars.$organisms-card-contentcard-details-text-color};
      --tag-text-color: #{vars.$organisms-card-contentcard-details-text-color};
    }

    &-card {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-5;
      padding: vars.$dimensions-d-2;
      background-color: vars.$organisms-card-contentcard-details-card-bg-color;
      border-radius: vars.$organisms-card-contentcard-subscription-details-border-radius;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
    }

    &-quotaRow {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      gap: vars.$dimensions-d-3;
    }

    &-usedSection {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: vars.$dimensions-d-2;
    }

    &-usedLabel {
      @include text.text('sm', vars.$typography-weight-strong);
      color: vars.$organisms-card-contentcard-details-label-color;
    }

    &-quotaValue {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: vars.$dimensions-d-1;
    }

    &-quotaNumber {
      @include text.text('lg', vars.$typography-weight-strong);
      color: vars.$organisms-card-contentcard-details-icon-remainingcredit-color;
    }

    &-quotaInfinity {
      color: vars.$organisms-card-contentcard-details-icon-remainingcredit-color;
    }

    &-period {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &-periodText {
      @include text.text('xs');
      color: vars.$organisms-card-contentcard-details-label-color;
    }

    &-footer {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: vars.$dimensions-d-3;
      padding-block-start: vars.$dimensions-d-3;
      border-block-start: vars.$dimensions-d-0-25 solid vars.$organisms-card-contentcard-details-text-color;
    }

    &-tripType {
      @include text.text('xs');
      flex: 1;
      color: vars.$organisms-card-contentcard-details-text-color;
    }

    &-resetInfo {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: vars.$dimensions-d-1;
    }

    &-resetIcon {
      color: vars.$organisms-card-contentcard-details-text-color;
    }

    &-resetText,
    &-resetDate {
      @include text.text('xs');
      color: vars.$organisms-card-contentcard-details-text-color;
    }
  }
}
</style>
